import { Country } from '@enmon/commons/lib/enmon';
import { Checkbox, type FormProps, Input, Row } from 'antd';
import { Form, type FormError, CountrySelect, InputNumber, Button, requiredTextRule } from 'components';
import dayjs from 'dayjs';
import type { CreateDemoUserDto } from 'lib/services';
import { type VFC, useState } from 'react';
import utc from 'dayjs/plugin/utc';
import type { StrictOmit } from '@enmon/commons';
import { generatePassword } from 'utils/generatePassword';
import { LocaleSelect } from 'components/form/select/LocaleSelect';

export const DEMO_ACCOUNT_FORM_ID = 'demo-account-form';

export type DemoAccountFormValues = StrictOmit<CreateDemoUserDto, 'expiresAt'> & {
  expiresAt: number;
};

export type DemoAccountProps = Pick<FormProps<CreateDemoUserDto>, 'onFinish'> & {
  errors?: readonly FormError[];
};

dayjs.extend(utc);

export const DemoAccountForm: VFC<DemoAccountProps> = ({ onFinish, errors }) => {
  const emailMustBeUniqueError = errors?.find(error => error.path.join('.').concat('.', error.type) === 'email.unique');
  const [demoAccountForm] = Form.useForm<DemoAccountFormValues>();
  const validity = Form.useWatch<DemoAccountFormValues['expiresAt']>('expiresAt', demoAccountForm);
  const expiresAt = validity ? dayjs.utc().add(validity, 'days').toISOString() : undefined;

  const [sendEmail, setSendEmail] = useState(false);

  const onReset = () => {
    demoAccountForm.resetFields(['password']);
  };

  const handleGeneratePasswordButtonClick = () => {
    demoAccountForm.setFieldValue('password', generatePassword(14));
  };

  return (
    <Form<DemoAccountFormValues>
      form={demoAccountForm}
      layout="vertical"
      name={DEMO_ACCOUNT_FORM_ID}
      onFinish={values => onFinish?.({ ...values, expiresAt })}
    >
      <Form.Item label="Name" name="name" rules={[requiredTextRule]}>
        <Input />
      </Form.Item>
      <Form.Item
        help={emailMustBeUniqueError && 'User account with this email already exists'}
        label="Email"
        name="email"
        rules={[
          { required: true, validateTrigger: 'onSubmit' },
          { type: 'email', validateTrigger: 'onSubmit' },
        ]}
        validateStatus={emailMustBeUniqueError ? 'error' : undefined}
      >
        <Input />
      </Form.Item>
      <Form.Item>
        <Checkbox onChange={() => setSendEmail(!sendEmail)} onClick={onReset}>
          Send welcome email automatically
        </Checkbox>
      </Form.Item>
      <Form.Item
        extra={
          <Row justify="end">
            <Button disabled={sendEmail} type="text" onClick={handleGeneratePasswordButtonClick}>
              Generate password
            </Button>
          </Row>
        }
        label="Password"
        name="password"
        rules={[{ required: !sendEmail }]}
      >
        <Input disabled={sendEmail} />
      </Form.Item>
      <Form.Item
        label="Validity of account"
        name="expiresAt"
        rules={[{ min: 7, max: 21, type: 'number', required: true, validateTrigger: ['onChange', 'onSubmit'] }]}
      >
        <InputNumber addonAfter="days" />
      </Form.Item>
      <Form.Item label="Country" name="country" rules={[{ required: true }]}>
        <CountrySelect showSearch options={[Country.CZ, Country.SK]} />
      </Form.Item>
      <Form.Item label="Language" name="locale" rules={[{ required: true }]}>
        <LocaleSelect showSearch />
      </Form.Item>
    </Form>
  );
};
